import { getLocale, intlLocale, t } from '@/i18n'

export function formatMoney(value: string | number, currency?: string): string {
  const amount = typeof value === 'number' ? value : Number(value)
  const formatted = new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0)
  return currency ? `${formatted} ${currency}` : formatted
}

export function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(intlLocale(), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

export function toDateTimeLocal(value?: string): string {
  const date = value ? new Date(value) : new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function fromDateTimeLocal(value: string): string {
  return new Date(value).toISOString()
}

export function formatQty(value: string | number): string {
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return String(value)
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 8 }).format(amount)
}

export function formatRate(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return '—'
  return new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount)
}

export function formatPct(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return '—'
  const sign = amount > 0 ? '+' : ''
  return `${sign}${new Intl.NumberFormat(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(amount)}%`
}

export function formatDay(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(intlLocale(), { dateStyle: 'short' }).format(date)
}

/** 1 угода, 2 угоди, 5 угод; en — deal/deals. */
export function formatOrders(count: number): string {
  const n = Math.abs(Math.trunc(Number.isFinite(count) ? count : 0))
  if (getLocale() === 'en') {
    return t(n === 1 ? 'fmt.orders.one' : 'fmt.orders.other', { n })
  }
  const mod10 = n % 10
  const mod100 = n % 100
  let key = 'fmt.orders.many'
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) key = 'fmt.orders.one'
    else if (mod10 >= 2 && mod10 <= 4) key = 'fmt.orders.few'
  }
  return t(key, { n })
}

export function multiply(a: string, b: string): number {
  const left = Number(parseAmount(a))
  const right = Number(parseAmount(b))
  if (!Number.isFinite(left) || !Number.isFinite(right)) return 0
  return Math.round(left * right * 10000) / 10000
}

/** Нормалізувати число з телефону: кома, пробіли, ₴, «3,80». Порожнє → ''. */
export function parseAmount(raw: string): string {
  let text = raw.trim().replace(/\u00a0/g, ' ')
  text = text.replace(/[０-９]/g, (digit) => String(digit.charCodeAt(0) - 0xff10))
  text = text.replace(/[₴$€zł]|PLN|UAH|USD|EUR|USDT|USDC/gi, '')
  text = text.replace(/\s+/g, '').replace(/'/g, '').replace(',', '.')
  if (!text || text === '-' || text === '.') return ''
  if (!/^-?\d+(\.\d+)?$/.test(text)) return ''
  return formatDecimalInput(text)
}

/** Прибрати хвіст нулів у полі форми: 1000.000000000000000000 → 1000, 3.8000 → 3.8.
 *  Не чіпає недописане значення на кшталт «3.» — його лишаємо як є. */
export function formatDecimalInput(raw: string): string {
  const text = raw.trim().replace(/\u00a0/g, '').replace(/\s/g, '').replace(',', '.')
  if (text === '' || text === '-' || text === '.' || text === '-.') return text
  if (!/^-?\d+(\.\d+)?$/.test(text)) return raw
  const negative = text.startsWith('-')
  const unsigned = negative ? text.slice(1) : text
  const [wholeRaw = '', fracRaw] = unsigned.split('.')
  const whole = wholeRaw.replace(/^0+(?=\d)/, '') || '0'
  const frac = (fracRaw ?? '').replace(/0+$/, '')
  const body = frac ? `${whole}.${frac}` : whole
  return negative ? `-${body}` : body
}
